const MIN_REASONABLE_YEAR = 2000;

const isReasonableDate = (date) =>
  date instanceof Date &&
  !Number.isNaN(date.getTime()) &&
  date.getUTCFullYear() >= MIN_REASONABLE_YEAR;

const looksNumeric = (value) =>
  typeof value === 'number' ||
  (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()));

const fromNumericTimestamp = (rawNumber) => {
  if (rawNumber === 0) return null;
  const abs = Math.abs(rawNumber);
  let millis = rawNumber;

  if (abs > 1e14 && abs < 1e18) {
    // Likely microseconds → convert to milliseconds
    millis = rawNumber / 1000;
  } else if (abs < 1e12) {
    // Likely seconds → convert to milliseconds
    millis = rawNumber * 1000;
  }

  const date = new Date(millis);
  return isReasonableDate(date) ? date : null;
};

export const parseTimestampToDate = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;

  if (raw instanceof Date) {
    return isReasonableDate(raw) ? raw : null;
  }

  if (looksNumeric(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || numeric === 0) {
      return null;
    }
    const numericDate = fromNumericTimestamp(numeric);
    if (numericDate) return numericDate;
  }

  const parsed = new Date(String(raw));
  return isReasonableDate(parsed) ? parsed : null;
};

export const resolveTimestampToDate = (...values) => {
  for (const value of values) {
    const date = parseTimestampToDate(value);
    if (date) return date;
  }
  return null;
};

const MESSAGE_TIMESTAMP_FIELDS = [
  'timestamp',
  'created_at',
  'updated_at',
  'sent_at',
  'timestamp_ms',
  'timestampMs',
  'timestamp_unix',
  'timestampUnix',
  'message_timestamp',
  'messageTimestamp',
  'ts',
  'time',
  'date',
  '_derivedTimestamp',
  '_derivedTimestampMs',
  '__derivedTimestamp',
  '__derivedTimestampMs',
  '_localTimestamp',
  'localTimestamp',
];

export const getMessageDate = (message, ...fallbacks) => {
  if (!message || typeof message !== 'object') {
    return resolveTimestampToDate(...fallbacks);
  }

  const candidates = MESSAGE_TIMESTAMP_FIELDS.map((field) => message[field]);
  return resolveTimestampToDate(...candidates, ...fallbacks);
};

export const toTimestampMilliseconds = (raw) => {
  const date = parseTimestampToDate(raw);
  return date ? date.getTime() : null;
};
